import * as React from "react";
import { toast } from "react-toastify";
import {
  useDispatch,
  useMappedState
} from "redux-react-hook";
import styled, { css } from "styled-components";
import { useCombobox } from "downshift";
import { useHistory } from "react-router-dom";

import {
  Text,
  TextTruncate
} from "src/library/components/Text";
import Dropdown from "src/library/components/Dropdown/Dropdown";
import LoadingSpinner from "src/library/components/LoadingSpinner/LoadingSpinner";
import {
  useDropsApi,
  IDrop
} from "src/core/drops/api/drops.api";
import { setDrop } from "src/core/drops/store/drops.actions";
import type { IStore } from "src/core/shared/store";
import { formatDropName } from "src/core/drops/utils/format-drop-name";
import { useEventListener } from "src/library/hooks";
import { setCurrentOrder } from "src/core/orders/store/orders.actions";
import { setCurrentList } from "src/core/lists/store/lists.actions";
import { useCustomerApi } from "src/core/customers/api/customers.api";

import useCurrentUser from "../../users/hooks/useCurrentUser";
import useDrop from "../hooks/useDrop";

export enum DropSwitcherTheme {
  Light = "light",
  Dark = "dark"
}
export interface DropSwitcherProps {
  dropSwitcherTheme?: DropSwitcherTheme;
  disableGetDropPageChange?: boolean;
  optionsFullWidth?: boolean;
}

const defaultPagination = {
  offset: 0,
  page: 0,
  limit: 30,
  sort: [ [ "name", "ASC" ] ]
};

/**
 * This component enables user to set the current drop context and is used by Admin and Customer routes.
 * If used by a customer route, primary state is updated by getUserDropsResponse
 * If used by admin route, primary state is updated by getDropsResponse
 */
const DropSwitcher: React.ReactFCWithChildren<DropSwitcherProps> = ({
  dropSwitcherTheme = DropSwitcherTheme.Dark, disableGetDropPageChange = false, optionsFullWidth = false
}) => {
  const dispatch = useDispatch();

  const {
    terminology, currentRole, inMasqueradeMode
  } = useMappedState((store: IStore) => ({
    terminology: store.global.settings?.terminology,
    currentRole: store.global.role,
    inMasqueradeMode: store.global.masqueradeMode?.active
  }));

  const [ { currentUser } ] = useCurrentUser();
  const history = useHistory();
  const [ isOpen, setIsOpen ] = React.useState(false);
  // used for a customer user
  const [ getUserDropsResponse, listUserDrops ] = useDropsApi("GET_USER_DROPS");
  const [ getDropResponse, getDrop ] = useDropsApi("GET_DROP");
  const [ userCustomerCount, getUserCustomerCount ] = useCustomerApi("GET_USER_CUSTOMER_COUNT");
  // used for an admin user
  const [ getDropsResponse, getDrops ] = useDropsApi("LIST_DROPS");
  const DropSwitcherRef = React.useRef<HTMLDivElement>(null);
  const [ { drop } ] = useDrop();
  const inputRef = React.createRef<HTMLInputElement>();
  const [ didInitialSearch, setDidInitialSearch ] = React.useState(false);
  const [ totalDrops, setTotalDrops ] = React.useState(0);
  const dropdownWrapperRef = React.useRef<HTMLDivElement>(null);
  const [ dropdownWidth, setDropdownWidth ] = React.useState<number>(230);
  const [ availableDrops, setAvailableDrops ] = React.useState<IDrop[] | null>(null);

  React.useEffect(() => {
    if (getDropResponse.data) {
      dispatch(setDrop(getDropResponse.data));

      if (!disableGetDropPageChange) {
        history.push("/dashboard");
      }
    }

    if (getDropResponse.error) {
      toast.error(`There was an error loading this ${terminology?.drop.singular.text}! ${getDropResponse.error.message}`);
    }
  }, [
    dispatch,
    getDropResponse,
    history,
    terminology?.drop.singular.text
  ]);

  React.useEffect(() => {
    if (currentUser?.id && !inMasqueradeMode) {
      getUserCustomerCount({ userId: currentUser.id });
    }
  }, [
    currentUser?.id,
    getUserCustomerCount,
    inMasqueradeMode
  ]);

  const customerCount = React.useMemo(() => {
    return userCustomerCount.data?.count ?? 1;
  }, [ userCustomerCount.data?.count ]);

  React.useEffect(() => {
    handleResizeWindow();

    document.addEventListener("click", event => {
      // @ts-ignore - Mouse events types are not correct :(
      if (DropSwitcherRef.current && !DropSwitcherRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    });

    return () => {
      document.removeEventListener("click", () => setIsOpen(false));
    };
  }, [ DropSwitcherRef ]);

  const downshiftProps = useCombobox<IDrop>({
    items: availableDrops || [],
    isOpen: true,
    onInputValueChange: ({ inputValue }) => {
      fetchDrops(inputValue);
    },
    itemToString: () => "",
    onSelectedItemChange: selectedDrop => {
      setIsOpen(false);
      // clear order and list from state on drop change
      dispatch(setCurrentOrder(null));
      dispatch(setCurrentList(null));

      if (selectedDrop.selectedItem?.id) {
        getDrop(selectedDrop.selectedItem.id as number);
      }
    }
  });

  // updates state when in the context of a customer user
  React.useEffect(() => {
    if (getUserDropsResponse.data) {
      setTotalDrops(getUserDropsResponse.data.total);
      setAvailableDrops(getUserDropsResponse.data.result);
    }
  }, [ getUserDropsResponse.data ]);

  // updates state when in the context of an admin user
  React.useEffect(() => {
    if (getDropsResponse.data) {
      setTotalDrops(getDropsResponse.data.count);
      setAvailableDrops(getDropsResponse.data.result);
    } else if (getDropsResponse.error) {
      toast.error(getDropsResponse.error.message);
    }
  }, [ getDropsResponse ]);

  // initial search
  React.useEffect(() => {
    if (!didInitialSearch) {
      if (currentUser) {
        // we're in the context of a customer user so list user drops
        setDidInitialSearch(true);

        listUserDrops({
          userId: currentUser?.id,
          filters: { status: { $ne: "deactivated" } },
          pagination: {
            offset: 0,
            page: 0,
            limit: 30,
            sort: [ [ "name", "ASC" ] ]
          }
        });
      } else {
        // otherwise, we're in the context of an admin user, so list drops
        getDrops({
          filters: {
            vanSalesOrdering: true,
            status: "active"
          },
          pagination: defaultPagination
        });
      }
    }
  }, [ currentUser, didInitialSearch ]);

  // listUserDrops if in the context of a customer user, otherwise getDrops for admin
  const fetchDrops = React.useCallback((searchTerm?: string) => {
    if (currentUser) {
      listUserDrops({
        userId: currentUser?.id,
        filters: { $or: [ { name: { $like: `%${searchTerm}%` } }, { branchNumber: { $like: `%${searchTerm}%` } } ] },
        pagination: {
          offset: 0,
          page: 0,
          limit: 30,
          sort: [ [ "name", "ASC" ] ]
        }
      });
    }

    if (currentRole?.type === "admin" && currentRole.code === "van-sales-admin") {
      getDrops({
        filters: {
          vanSalesOrdering: true,
          $or: [ { name: { $like: `%${searchTerm}%` } }, { branchNumber: { $like: `%${searchTerm}%` } } ],
          status: "active"
        },
        pagination: defaultPagination
      });
    }
  }, [ currentUser, currentRole ]);

  const handleResizeWindow = React.useCallback(() => {
    if (dropdownWrapperRef.current && optionsFullWidth) {
      const dropdownBounds = dropdownWrapperRef.current.getBoundingClientRect();

      setDropdownWidth(dropdownBounds.width - 20);
    }
  }, [ optionsFullWidth ]);

  useEventListener("resize", handleResizeWindow);
  const menuProps = downshiftProps.getMenuProps({}, { suppressRefError: true });

  const inputProps = downshiftProps.getInputProps({
    className: "drop-search-input",
    id: "drop-search",
    placeholder: `Filter ${terminology?.drop.plural.text}`
  }, { suppressRefError: true });

  if (currentUser) {
    return (
      <DropSwitcherWrapper
        dropSwitcherTheme={dropSwitcherTheme}
        ref={dropdownWrapperRef}
      >
        {(isOpen || totalDrops > 1) ? (
          <Dropdown
            title={(drop) ? (
              <>
                <TextTruncate
                  truncateProps={{ lines: 1 }}
                  weight="bold"
                >
                  {drop.customer?.name}
                </TextTruncate>

                <br />

                <div className="drop-name">
                  <TextTruncate
                    truncateProps={{ lines: 3 }}
                    weight="light"
                  >
                    {formatDropName(drop, true)}
                  </TextTruncate>
                </div>
              </>
            ) : (
              <div className="drop-name">
                <TextTruncate
                  truncateProps={{ lines: 1 }}
                  weight="light"
                >
                  {drop ? formatDropName(drop) : "Please select a drop"}
                </TextTruncate>
              </div>
            )}
            toggleOpen={newIsOpen => {
              const opening = typeof newIsOpen === "boolean" ? newIsOpen : !isOpen;

              if (opening && (!getUserDropsResponse.data?.result.length || !getDropsResponse.data?.result.length)) {
                fetchDrops("");
              } else {
                downshiftProps.reset();
              }

              setTimeout(() => {
                if (inputRef.current) {
                  inputRef.current.focus();
                }
              }, 100);
              setIsOpen(typeof newIsOpen === "boolean" ? newIsOpen : !isOpen);
            }}
            isOpen={isOpen}
          >
            <DropSwitcherInner dropdownWidth={dropdownWidth}>
              {totalDrops && (
                <div className="switcher-header">
                  <Text
                    color="secondary"
                    fontSize="sub"
                  >
                    {availableDrops?.length === totalDrops ?
                      `${totalDrops} ${terminology?.drop.plural.text} available` :
                      `Showing ${availableDrops?.length} ${terminology?.drop.plural.text}`}
                  </Text>
                </div>
              )}

              <div>
                <input
                  ref={inputRef}
                  {...inputProps}
                />
              </div>

              <div
                className="search-results-wrapper"
                {...menuProps}
              >
                {(getUserDropsResponse.pending || getDropsResponse.pending) && (
                  <LoadingSpinner
                    type="flat"
                    position="inline"
                    size={5}
                  />
                )}

                <ul
                  className="search-results"

                >
                  {availableDrops && availableDrops.map((item, index) => (
                    <div
                      key={`${item.branchNumber}-${index}`}
                      className={`${item.status} drop-item ${downshiftProps.highlightedIndex === index ? "highlighted" : ""} ${item.id === drop?.id ? "selected" : ""}`}
                      {...downshiftProps.getItemProps({
                        item,
                        index
                      })}
                    >
                      <Text className="name">
                        {`${item.status !== "active" ? item.status + " - " : ""}${formatDropName(item)}`}
                      </Text>
                    </div>
                  ))}
                </ul>
              </div>
            </DropSwitcherInner>
          </Dropdown>
        ) : (
          <div className="single-drop">
            {(getUserDropsResponse.pending || getDropsResponse.pending) && (
              <div className="drop-loader">
                <LoadingSpinner
                  type="flat"
                  position="inline"
                  size={5}
                />
              </div>
            )}

            {customerCount > 1 && (
              <TextTruncate weight="bold">
                {drop?.customer?.name}
              </TextTruncate>
            )}

            <TextTruncate
              truncateProps={{ lines: 1 }}
              className="name"
              weight="light"
            >
              {drop && formatDropName(drop)}
            </TextTruncate>
          </div>
        )}
      </DropSwitcherWrapper>
    );
  }

  return null;
};

export default DropSwitcher;

const DropSwitcherWrapper = styled.div<{ dropSwitcherTheme: DropSwitcherTheme }>`
  ${({ theme, dropSwitcherTheme }) => css`
    margin: ${theme.spacing.margins.sm} ${theme.spacing.margins.sm};
    color: ${theme.colors.white};

    .single-drop {
      text-align: left;
      display: block;
      flex-grow: true;
      width: 100%;
      border: 1px solid ${theme.colors.white};
      padding: ${theme.spacing.padding.xs} ${theme.spacing.padding.sm};
      border-radius: ${theme.borderRadius};
      max-width: 350px;

        > span {
          display: block;
          color: ${theme.colors.white};
        }

      .drop-loader {
        height: 20px;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }

  

    .dropdown-select {
      background: transparent;
      margin: auto;
      width: 100%;
      max-height: initial;
      padding-left: ${theme.spacing.padding.sm};
      padding-top: ${theme.spacing.padding.xs};
      padding-bottom: ${theme.spacing.padding.xs};
      border: 1px solid ${theme.colors.white};

      .dropdown-title span { 
        color: ${theme.colors.white};
      }

      svg {
        fill: ${theme.colors.white} !important;
      }

      &:hover,
      .active {
        cursor: pointer;
        border-color: ${theme.colors.white};

        .dropdown-title { 
          font-weight: ${theme.fonts.weights.bold};
        }

        svg {
          fill: ${theme.colors.accentLight};
        }
      }

      .dropdown-title {
        color: ${theme.colors.white};
      }
    }

    ${dropSwitcherTheme === DropSwitcherTheme.Light && css`
      color: ${theme.colors.primaryDark};

      .single-drop {
        border: 1px solid ${theme.colors.primaryDark};

        > span {
          display: block;
          color: ${theme.colors.white};
        }
      }

      .dropdown-select {
        border: 1px solid ${theme.colors.primaryDark};

        .dropdown-title span {
          color: ${theme.colors.primaryDark};
        }

        svg {
          fill: ${theme.colors.primaryDark} !important;
        }

        &:hover,
        .active  {
          border-color: ${theme.colors.primaryDark};

          svg {
            fill: ${theme.colors.primaryDark};
          }
        }
      }      
    `}
  `}
`;

const DropSwitcherInner = styled.div<{ dropdownWidth?: number }>`
  ${({ theme, dropdownWidth }) => css`
    width: ${dropdownWidth ? dropdownWidth : 230}px;
    max-width: ${dropdownWidth ? dropdownWidth : 230}px;

    .switcher-header {
      padding: ${theme.spacing.padding.sm};
      display: block;
      width: 100%;
    }

    .drop-search-input {
      height: 40px;
      padding: 10px 15px;
      outline: none;
      border: none;
      width: 100%;
      border-top: 1px solid ${theme.colors.lightGrey};
      border-bottom: 1px solid ${theme.colors.lightGrey};
    }

    .drop-item {
      padding: ${theme.spacing.padding.sm} ${theme.spacing.padding.md};
      border-bottom: 1px solid ${theme.colors.lightGrey};
      
      &.suspended {
        background: ${theme.colors.error};
        &.highlighted, &.selected {
          background: ${theme.colors.error};
        }
      }
      &:hover {
        cursor: pointer;
      } 

      &.highlighted, &.selected {
        background: ${theme.colors.lightestGrey};

        .name {
          font-weight: ${theme.fonts.weights.bold};
        }
      }

      &.highlighted {
        background: ${theme.colors.lightGrey};
      }
    }
  `}
`;
